import React, { useState } from 'react';
import {
  ChevronDown, Sparkles, MapPin, Waves, Utensils, Bus, AlertTriangle, CalendarDays
} from 'lucide-react';

/**
 * TravelCompanion — Parts 2 & 3 of the 8-part travel-assistant spec.
 *
 * Renders 6 collapsible sections below an AI trip card:
 *   A. Why this destination
 *   B. Top things to do
 *   C. Best beaches (suppressed when empty)
 *   D. Food & restaurants
 *   E. Transportation
 *   F. Mistakes to avoid
 *
 * Plus a "Create Daily Plan" CTA that dispatches a follow-up chat query
 * routing to the existing concierge ITINERARY archetype (iter57).
 *
 * Design goals:
 *   - Mobile-first: every section starts collapsed, one tap to expand.
 *   - "Why this destination" expanded by default — it's the most-skimmed.
 *   - Max 3-5 entries per section enforced server-side in
 *     services/travel_companion.py:_clamp_*.
 *
 * Props:
 *   companion — { whyDestination[], topThingsToDo[], bestBeaches[],
 *                 foodRestaurants[], transportation[], mistakesToAvoid[] }
 *   dailyPlanAvailable — bool; gates the "Create Daily Plan" CTA visibility
 *   destination — string used to assemble the daily-plan chat query
 *   nights — number of nights for daily-plan default
 *   onSendQuery(text) — handler to fire a follow-up chat query
 */
export default function TravelCompanion({
  companion,
  dailyPlanAvailable,
  destination,
  nights,
  onSendQuery,
}) {
  // Hooks ALWAYS before the early return.
  const [openSection, setOpenSection] = useState('whyDestination');

  if (!companion || typeof companion !== 'object') {
    // The daily-plan CTA can still appear on its own.
    if (!dailyPlanAvailable) return null;
  }

  const c = companion || {};
  const hasAny = (
    (c.whyDestination?.length) ||
    (c.topThingsToDo?.length) ||
    (c.bestBeaches?.length) ||
    (c.foodRestaurants?.length) ||
    (c.transportation?.length) ||
    (c.mistakesToAvoid?.length)
  );

  if (!hasAny && !dailyPlanAvailable) return null;

  const toggle = (key) => setOpenSection((prev) => (prev === key ? null : key));

  const handleDailyPlanClick = () => {
    if (!onSendQuery) return;
    const days = Math.max(2, Math.min(7, parseInt(nights, 10) || 3));
    const destShort = (destination || '').split(',')[0].trim() || 'this destination';
    onSendQuery(`Create a ${days}-day daily plan for ${destShort}.`);
  };

  return (
    <div className="mt-3 mb-2" data-testid="travel-companion">
      {/* Header chip */}
      <div className="flex items-center gap-1.5 mb-2 text-[11px] uppercase tracking-wider text-indigo-700 font-semibold px-1">
        <Sparkles className="w-3 h-3" />
        Trip Companion
      </div>

      {/* Section A — Why this destination (default open) */}
      <SectionBlock
        keyName="whyDestination"
        icon={Sparkles}
        title="Why this destination"
        items={c.whyDestination}
        isOpen={openSection === 'whyDestination'}
        onToggle={toggle}
        bullet
      />

      {/* Section B — Top things to do */}
      <SectionBlock
        keyName="topThingsToDo"
        icon={MapPin}
        title="Top things to do"
        items={c.topThingsToDo}
        isOpen={openSection === 'topThingsToDo'}
        onToggle={toggle}
      />

      {/* Section C — Best beaches (only if items exist; iter59 server-side suppression) */}
      {c.bestBeaches?.length > 0 && (
        <SectionBlock
          keyName="bestBeaches"
          icon={Waves}
          title="Best beaches"
          items={c.bestBeaches}
          isOpen={openSection === 'bestBeaches'}
          onToggle={toggle}
        />
      )}

      {/* Section D — Food & restaurants */}
      <SectionBlock
        keyName="foodRestaurants"
        icon={Utensils}
        title="Food & restaurants"
        items={c.foodRestaurants}
        isOpen={openSection === 'foodRestaurants'}
        onToggle={toggle}
      />

      {/* Section E — Transportation */}
      <SectionBlock
        keyName="transportation"
        icon={Bus}
        title="Transportation"
        items={c.transportation}
        isOpen={openSection === 'transportation'}
        onToggle={toggle}
        bullet
      />

      {/* Section F — Mistakes to avoid */}
      <SectionBlock
        keyName="mistakesToAvoid"
        icon={AlertTriangle}
        title="Mistakes to avoid"
        items={c.mistakesToAvoid}
        isOpen={openSection === 'mistakesToAvoid'}
        onToggle={toggle}
        bullet
        warning
      />

      {/* Daily Plan CTA — Part 3 of the spec */}
      {dailyPlanAvailable && (
        <button
          type="button"
          onClick={handleDailyPlanClick}
          data-testid="companion-daily-plan-cta"
          className="
            mt-3 w-full inline-flex items-center justify-center gap-2
            bg-blue-600 hover:bg-blue-700
            text-white font-semibold px-4 py-3 rounded-xl transition-all
            shadow-md shadow-blue-600/25 hover:shadow-lg hover:shadow-blue-600/30
            focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-2
            min-h-[44px] text-sm
          "
        >
          <CalendarDays className="w-4 h-4" />
          Create Daily Plan
        </button>
      )}
    </div>
  );
}


/**
 * SectionBlock — one collapsible card. `bullet=true` renders items as a
 * simple bullet list; default renders {title, oneLiner} cards.
 */
function SectionBlock({
  keyName, icon: Icon, title, items, isOpen, onToggle, bullet, warning,
}) {
  if (!items || !Array.isArray(items) || items.length === 0) return null;

  const headerColor = warning ? 'text-amber-700' : 'text-indigo-700';
  const iconBg = warning ? 'bg-amber-100' : 'bg-indigo-100';
  const iconFg = warning ? 'text-amber-600' : 'text-indigo-600';
  const border = warning ? 'border-amber-200' : 'border-indigo-200';
  const bgOpen = warning ? 'bg-amber-50/50' : 'bg-indigo-50/40';

  return (
    <div
      data-testid={`companion-section-${keyName}`}
      className={`mb-2 rounded-xl border ${border} ${isOpen ? bgOpen : 'bg-white'} overflow-hidden transition-colors`}
    >
      <button
        type="button"
        onClick={() => onToggle(keyName)}
        data-testid={`companion-toggle-${keyName}`}
        aria-expanded={isOpen}
        className="w-full flex items-center justify-between px-3 py-2.5 text-left hover:bg-slate-50/50 transition-colors"
      >
        <div className="flex items-center gap-2 min-w-0">
          <div className={`w-6 h-6 rounded-full ${iconBg} flex items-center justify-center flex-shrink-0`}>
            <Icon className={`w-3.5 h-3.5 ${iconFg}`} />
          </div>
          <span className={`text-sm font-bold ${headerColor}`}>{title}</span>
          <span className="text-[10px] text-slate-400 font-semibold">({items.length})</span>
        </div>
        <ChevronDown
          className={`w-4 h-4 text-slate-400 flex-shrink-0 transition-transform ${isOpen ? 'rotate-180' : ''}`}
        />
      </button>

      {isOpen && (
        <div className="px-3 pb-3 pt-1" data-testid={`companion-body-${keyName}`}>
          {bullet ? (
            <ul className="space-y-1.5">
              {items.map((item, i) => (
                <li key={`${keyName}-${i}`} className="flex items-start gap-2 text-[13px] text-slate-700 leading-relaxed">
                  <span className={`mt-1.5 w-1 h-1 rounded-full flex-shrink-0 ${warning ? 'bg-amber-500' : 'bg-indigo-500'}`} />
                  <span>{typeof item === 'string' ? item : (item.title || item.oneLiner || '')}</span>
                </li>
              ))}
            </ul>
          ) : (
            <div className="space-y-2">
              {items.map((item, i) => (
                <div key={`${keyName}-${i}`} className="text-[13px]">
                  <div className="font-bold text-slate-900 leading-snug">
                    {typeof item === 'string' ? item : item.title}
                  </div>
                  {typeof item !== 'string' && item.oneLiner && (
                    <div className="text-slate-600 mt-0.5 leading-relaxed">{item.oneLiner}</div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
